import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Star,
  ThumbsUp,
  MessageSquare,
  CheckCircle,
  Camera,
  Send,
  Clock,
  User,
  Scissors,
  Award,
  AlertCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

// Star Rating Component
const StarRating = ({ rating, onRatingChange, size = "md", readonly = false, label = "" }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8"
  };

  return (
    <div className="flex flex-col gap-1">
      {label && <span className="text-sm text-gray-400">{label}</span>}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={readonly}
            onClick={() => !readonly && onRatingChange?.(star)}
            onMouseEnter={() => !readonly && setHoverRating(star)}
            onMouseLeave={() => !readonly && setHoverRating(0)}
            className={`transition-transform ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
          >
            <Star
              className={`${sizeClasses[size]} ${
                star <= (hoverRating || rating)
                  ? "fill-amber-400 text-amber-400"
                  : "text-gray-600"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

// Review Card Component
const ReviewCard = ({ review, onHelpful, onRespond, isStylist = false }) => {
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [response, setResponse] = useState("");

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <Card className="bg-stone-900 border-stone-800">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-black font-bold">
              {review.client_name?.charAt(0) || "?"}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">{review.client_name}</span>
                {review.verified_booking && (
                  <Badge className="bg-green-500/20 text-green-400 text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>{formatDate(review.created_at)}</span>
                {review.service_name && (
                  <>
                    <span>•</span>
                    <span>{review.service_name}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <StarRating rating={review.rating} readonly size="sm" />
        </div>

        {/* Rating Breakdown */}
        <div className="flex gap-4 mb-3 text-sm">
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Service:</span>
            <span className="text-amber-400">{review.service_quality}/5</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Professional:</span>
            <span className="text-amber-400">{review.professionalism}/5</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Punctual:</span>
            <span className="text-amber-400">{review.punctuality}/5</span>
          </div>
        </div>

        {/* Comment */}
        {review.comment && (
          <p className="text-gray-300 mb-3">{review.comment}</p>
        )}

        {/* Photos */}
        {review.photos?.length > 0 && (
          <div className="flex gap-2 mb-3 overflow-x-auto">
            {review.photos.map((photo, i) => (
              <img 
                key={i} 
                src={photo} 
                alt={`Review photo ${i+1}`}
                className="w-20 h-20 rounded-lg object-cover"
              />
            ))}
          </div>
        )}

        {/* Recommend Badge */}
        {review.recommend && (
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-green-500/20 text-green-400">
              <ThumbsUp className="w-3 h-3 mr-1" />
              Would recommend
            </Badge>
          </div>
        )}

        {/* Stylist Response */}
        {review.stylist_response && (
          <div className="bg-stone-800 rounded-lg p-3 mt-3 border-l-2 border-amber-500">
            <div className="flex items-center gap-2 mb-2">
              <Scissors className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-medium text-sm">Stylist Response</span>
              <span className="text-gray-500 text-xs">{formatDate(review.responded_at)}</span>
            </div>
            <p className="text-gray-300 text-sm">{review.stylist_response}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-800">
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-gray-400 hover:text-white"
            onClick={() => onHelpful?.(review.review_id)}
          >
            <ThumbsUp className="w-4 h-4 mr-1" />
            Helpful ({review.helpful_count || 0})
          </Button>

          {isStylist && !review.stylist_response && (
            <Button
              variant="ghost"
              size="sm"
              className="text-amber-400 hover:text-amber-300"
              onClick={() => setShowResponseForm(!showResponseForm)}
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              Respond
            </Button>
          )}
        </div>

        {/* Response Form */}
        {showResponseForm && (
          <div className="mt-3 pt-3 border-t border-stone-800">
            <Textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Write your response..."
              className="bg-stone-800 border-stone-700 text-white mb-2"
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowResponseForm(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-black"
                onClick={() => {
                  onRespond?.(review.review_id, response);
                  setShowResponseForm(false);
                  setResponse("");
                }}
              >
                <Send className="w-4 h-4 mr-1" />
                Send Response
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Write Review Modal
export const WriteReviewModal = ({ 
  isOpen, 
  onClose, 
  appointment, 
  onSubmit 
}) => {
  const [rating, setRating] = useState(5);
  const [professionalism, setProfessionalism] = useState(5);
  const [punctuality, setPunctuality] = useState(5);
  const [serviceQuality, setServiceQuality] = useState(5);
  const [comment, setComment] = useState("");
  const [recommend, setRecommend] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!appointment) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/api/reviews`, {
        appointment_id: appointment.appointment_id,
        rating,
        professionalism,
        punctuality,
        service_quality: serviceQuality,
        comment,
        recommend,
        photos: []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(res.data.message || "Review submitted!");
      onSubmit?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400" />
            Write a Review
          </DialogTitle>
        </DialogHeader>

        {appointment && (
          <div className="space-y-6 py-4">
            {/* Appointment Info */}
            <div className="bg-stone-800 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-black font-bold text-lg">
                  {appointment.stylist_name?.charAt(0) || "?"}
                </div>
                <div>
                  <p className="text-white font-medium">{appointment.stylist_name}</p>
                  <p className="text-gray-400 text-sm">{appointment.service_name}</p>
                </div>
              </div>
            </div>

            {/* Overall Rating */}
            <div className="text-center">
              <p className="text-gray-400 mb-2">Overall Experience</p>
              <div className="flex justify-center">
                <StarRating rating={rating} onRatingChange={setRating} size="lg" />
              </div>
            </div>

            {/* Detailed Ratings */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <StarRating 
                  rating={serviceQuality} 
                  onRatingChange={setServiceQuality} 
                  size="sm" 
                  label="Service Quality"
                />
              </div>
              <div className="text-center">
                <StarRating 
                  rating={professionalism} 
                  onRatingChange={setProfessionalism} 
                  size="sm" 
                  label="Professionalism"
                />
              </div>
              <div className="text-center">
                <StarRating 
                  rating={punctuality} 
                  onRatingChange={setPunctuality} 
                  size="sm" 
                  label="Punctuality"
                />
              </div>
            </div>

            {/* Comment */}
            <div>
              <label className="text-sm text-gray-400 block mb-2">Your Review (Optional)</label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your experience..."
                className="bg-stone-800 border-stone-700 text-white"
                rows={4}
              />
            </div>

            {/* Recommend */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="recommend"
                checked={recommend}
                onChange={(e) => setRecommend(e.target.checked)}
                className="rounded border-stone-700 bg-stone-800 text-amber-500"
              />
              <label htmlFor="recommend" className="text-gray-300 cursor-pointer">
                I would recommend this stylist to others
              </label>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-gray-400">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="submit-review-btn"
          >
            {submitting ? "Submitting..." : "Submit Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Reviews List Component (for stylist profiles)
export const ReviewsList = ({ stylistId, isStylist = false }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/api/reviews/stylist/${stylistId}`);
      setReviews(res.data.reviews || []);
      setStats({
        average: res.data.average_rating,
        total: res.data.total_reviews,
        breakdown: res.data.rating_breakdown
      });
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (stylistId) fetchReviews();
  }, [stylistId]);

  const handleHelpful = async (reviewId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/reviews/${reviewId}/helpful`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Marked as helpful");
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as helpful");
    }
  };

  const handleRespond = async (reviewId, response) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/reviews/${reviewId}/respond`, 
        { stylist_response: response },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Response added");
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add response");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      {stats && (
        <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="flex items-center gap-3">
                  <span className="text-5xl font-bold text-white">{stats.average?.toFixed(1) || "0.0"}</span>
                  <div>
                    <StarRating rating={Math.round(stats.average || 0)} readonly size="md" />
                    <p className="text-gray-400 text-sm mt-1">{stats.total || 0} reviews</p>
                  </div>
                </div>
              </div>
              
              {stats.breakdown && (
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-2xl font-bold text-amber-400">{stats.breakdown.service_quality}</p>
                    <p className="text-gray-400 text-xs">Service</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-400">{stats.breakdown.professionalism}</p>
                    <p className="text-gray-400 text-xs">Professional</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-400">{stats.breakdown.punctuality}</p>
                    <p className="text-gray-400 text-xs">Punctuality</p>
                  </div>
                </div>
              )}

              {stats.breakdown?.recommend_percentage > 0 && (
                <Badge className="bg-green-500/20 text-green-400">
                  <ThumbsUp className="w-4 h-4 mr-1" />
                  {stats.breakdown.recommend_percentage}% recommend
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reviews List */}
      {reviews.length > 0 ? (
        <div className="space-y-4">
          {reviews.map((review) => (
            <ReviewCard
              key={review.review_id}
              review={review}
              onHelpful={handleHelpful}
              onRespond={handleRespond}
              isStylist={isStylist}
            />
          ))}
        </div>
      ) : (
        <Card className="bg-stone-900 border-stone-800">
          <CardContent className="p-8 text-center">
            <Star className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No reviews yet</p>
            <p className="text-gray-500 text-sm mt-1">Be the first to leave a review!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Pending Reviews Component (for client dashboard)
export const PendingReviews = ({ onReviewComplete }) => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const fetchPending = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/reviews/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPending(res.data.pending_reviews || []);
    } catch (error) {
      console.error("Failed to fetch pending reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const handleReviewSubmit = () => {
    fetchPending();
    onReviewComplete?.();
  };

  if (loading) return null;
  if (pending.length === 0) return null;

  return (
    <>
      <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30 mb-6">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400" />
            Rate Your Recent Visits
            <Badge className="bg-amber-500 text-black ml-2">{pending.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {pending.slice(0, 3).map((apt) => (
              <div 
                key={apt.appointment_id}
                className="flex items-center justify-between bg-stone-800 rounded-lg p-3"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-black font-bold">
                    {apt.stylist_name?.charAt(0) || "?"}
                  </div>
                  <div>
                    <p className="text-white font-medium">{apt.stylist_name}</p>
                    <p className="text-gray-400 text-sm">{apt.service_name}</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                  onClick={() => {
                    setSelectedAppointment(apt);
                    setShowReviewModal(true);
                  }}
                  data-testid={`write-review-${apt.appointment_id}`}
                >
                  <Star className="w-4 h-4 mr-1" />
                  Review
                </Button>
              </div>
            ))}
          </div>
          {pending.length > 3 && (
            <p className="text-gray-400 text-sm mt-3 text-center">
              +{pending.length - 3} more appointments to review
            </p>
          )}
        </CardContent>
      </Card>

      <WriteReviewModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onSubmit={handleReviewSubmit}
      />
    </>
  );
};

export default ReviewsList;
